import React from 'react';
import type { TimelineItem, TimelineEffects } from './Timeline';

type Props = {
  items: TimelineItem[];
  variant?: 'classic' | 'dots' | 'cards' | 'split';
  scrollable?: boolean;
  expandable?: boolean;
  effects?: TimelineEffects;
  onChange: (next: { items: TimelineItem[]; variant?: 'classic' | 'dots' | 'cards' | 'split'; scrollable?: boolean; expandable?: boolean; effects?: TimelineEffects }) => void;
};

const label = 'text-xs font-medium text-gray-600';
const input = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500';

const TimelineSettings: React.FC<Props> = ({ items, variant = 'classic', scrollable: scrollIn = false, expandable: expandIn = false, effects, onChange }) => {
  const [list, setList] = React.useState<TimelineItem[]>(items);
  const [style, setStyle] = React.useState<Props['variant']>(variant);
  const [scrollable, setScrollable] = React.useState<boolean>(!!scrollIn);
  const [expandable, setExpandable] = React.useState<boolean>(!!expandIn);
  const [hover, setHover] = React.useState<TimelineEffects['hover']>(effects?.hover ?? 'lift');
  const [revealSpeed, setRevealSpeed] = React.useState<TimelineEffects['revealSpeed']>(effects?.revealSpeed ?? 'normal');

  // Initialize once from props; keep local while modal is open to avoid caret jumps
  // If we ever need to explicitly reset from parent, we can add a resetKey prop.

  const emit = (nextItems: TimelineItem[], nextVariant: Props['variant']) => {
    setList(nextItems);
    setStyle(nextVariant);
    onChange({ items: nextItems, variant: nextVariant, scrollable, expandable, effects: { hover, revealSpeed } });
  };

  const updateItem = (i: number, patch: Partial<TimelineItem>) => {
    const next = list.map((it, idx) => idx === i ? { ...it, ...patch } : it);
    emit(next, style);
  };
  const addItem = () => {
    const next = [...list, { date: new Date().toISOString().slice(0,10), title: 'New item', description: '' }];
    emit(next, style);
  };
  const removeItem = (i: number) => {
    const next = list.filter((_, idx) => idx !== i);
    emit(next, style);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className={label}>Timeline style</label>
        <div className="mt-2">
          <div className="relative inline-block">
            <select
              value={style}
              onChange={(e)=> emit(list, e.target.value as any)}
              className="appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-9 text-sm capitalize outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="classic">Classic</option>
              <option value="dots">Dots</option>
              <option value="cards">Cards</option>
              <option value="split">Split (Alternating)</option>
            </select>
            <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-500">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </span>
          </div>
        </div>
      </div>

      <div>
        <label className={label}>Scrolling</label>
        <div className="mt-2 flex items-center justify-between rounded-lg border border-gray-200 p-3">
          <div>
            <div className="text-sm font-medium text-gray-900">Make timeline scrollable</div>
            <div className="text-xs text-gray-500">Helpful when you have many items; constrains height and enables smooth scrolling.</div>
          </div>
          <button
            type="button"
            onClick={() => { const next = !scrollable; setScrollable(next); onChange({ items: list, variant: style, scrollable: next, expandable, effects: { hover, revealSpeed } }); }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${scrollable ? 'bg-purple-600' : 'bg-gray-300'}`}
            aria-pressed={scrollable}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${scrollable ? 'translate-x-5' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      <div>
        <label className={label}>Expandable cards</label>
        <div className="mt-2 flex items-center justify-between rounded-lg border border-gray-200 p-3">
          <div>
            <div className="text-sm font-medium text-gray-900">Allow items to expand for more content</div>
            <div className="text-xs text-gray-500">Adds a chevron to cards; unfolded content appears below the summary.</div>
          </div>
          <button
            type="button"
            onClick={() => { const next = !expandable; setExpandable(next); onChange({ items: list, variant: style, scrollable, expandable: next, effects: { hover, revealSpeed } }); }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${expandable ? 'bg-purple-600' : 'bg-gray-300'}`}
            aria-pressed={expandable}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${expandable ? 'translate-x-5' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      <div>
        <label className={label}>Effects</label>
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-sm font-medium text-gray-900">Hover style</div>
            <select
              className="mt-2 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              value={hover}
              onChange={(e)=> { const v = e.target.value as TimelineEffects['hover']; setHover(v); onChange({ items: list, variant: style, scrollable, expandable, effects: { hover: v, revealSpeed } }); }}
            >
              <option value="none">None</option>
              <option value="lift">Lift</option>
              <option value="glow">Glow</option>
              <option value="scale">Scale</option>
            </select>
          </div>
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-sm font-medium text-gray-900">Reveal speed</div>
            <select
              className="mt-2 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              value={revealSpeed}
              onChange={(e)=> { const v = e.target.value as TimelineEffects['revealSpeed']; setRevealSpeed(v); onChange({ items: list, variant: style, scrollable, expandable, effects: { hover, revealSpeed: v } }); }}
            >
              <option value="fast">Fast</option>
              <option value="normal">Normal</option>
              <option value="slow">Slow</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <label className={label}>Items</label>
        <div className="mt-2 space-y-3">
          {list.map((it, i) => (
            <div key={i} className="rounded-xl border border-gray-200 p-3">
              <div className="grid grid-cols-3 gap-2">
                <input className={input} value={it.date || ''} onChange={(e)=> updateItem(i, { date: e.target.value })} placeholder="YYYY-MM-DD" />
                <input className={input} value={it.title || ''} onChange={(e)=> updateItem(i, { title: e.target.value })} placeholder="Title" />
                <input className={input} value={it.description || ''} onChange={(e)=> updateItem(i, { description: e.target.value })} placeholder="Description" />
              </div>
              {expandable && (
                <div className="mt-2">
                  <textarea className={input} rows={3} value={it.details || ''} onChange={(e)=> updateItem(i, { details: e.target.value })} placeholder="Expanded content (optional)" />
                </div>
              )}
              <div className="mt-2 flex items-center justify-end">
                <button className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50" onClick={()=> removeItem(i)}>
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m-1 0l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                  Remove
                </button>
              </div>
            </div>
          ))}
          <div>
            <button className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-purple-700 hover:bg-purple-50" onClick={addItem}>
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
              Add item
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineSettings;
